import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import shiftPlanReducer, { initialState as shiftPlanInitialState } from './slices/shiftPlanSlice';
import rosterAndRatesReducer from './slices/rosterAndRatesSlice';
import reviewAndPublishReducer from './slices/reviewAndPublishSlice';
import volumeAndWipReducer from './slices/volumesAndWipSlice';
import errorReducer from './slices/errorSlice';
import getAllShiftPlansReducer from './slices/GetAllShiftPlansSlice';
import navigationReducer from './slices/navigationSlice';
import themeReducer from './slices/themeSlice';
import settingsReducer from './slices/settingsSlice';
import stationAssignmentReducer from './slices/stationAssignmentSlice';
import siteLayoutReducer from './slices/siteLayoutSlice';
import certificateRulesReducer from './slices/certificateRulesSlice';
import workItemDetailReducer from '../pages/workItemDetail/slice';
import checkoutReducer from './slices/checkoutSlice';
import publishedShiftPlanFeatureReducer from './slices/v2/publishedShiftPlanFeatureSlice';
import midShiftReplanFeatureReducer from './slices/v2/midShiftReplanFeatureSlice';
import { RootState } from './types';
import { getStationCodeFromUrl } from '../utils/urlStationCode';

// Read stationCode from URL synchronously before React mounts, so all
// slices have it available on the very first render — no race with useEffect.
const initialStationCode = getStationCodeFromUrl();

// Configure the store
export const store = configureStore({
  reducer: {
    shiftPlan: shiftPlanReducer,
    rosterAndRates: rosterAndRatesReducer,
    reviewAndPublish: reviewAndPublishReducer,
    volumesAndWip: volumeAndWipReducer,
    error: errorReducer,
    getAllShiftPlans: getAllShiftPlansReducer,
    navigation: navigationReducer,
    theme: themeReducer,
    settings: settingsReducer,
    stationAssignment: stationAssignmentReducer,
    siteLayout: siteLayoutReducer,
    certificateRules: certificateRulesReducer,
    workItemDetail: workItemDetailReducer,
    checkout: checkoutReducer,
    publishedShiftPlanFeature: publishedShiftPlanFeatureReducer,
    midShiftReplanFeature: midShiftReplanFeatureReducer,
  },
  preloadedState: initialStationCode ? {
    shiftPlan: {
      ...shiftPlanInitialState,
      stationCode: initialStationCode,
    },
  } : undefined,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [],
      },
    }),
});

// Export store types
export type AppDispatch = typeof store.dispatch;

// Export typed hooks for use throughout the app
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Export store as default
export default store;
